import i18next, { type i18n as I18nType } from "i18next";
import { initReactI18next } from "react-i18next";

// Korean first-paint subset (boot / login form / app shell / error
// toasts). Statically imported (eager, stays in the entry chunk,
// ~3.3 KB gz) so the ko-default majority paints the boot screen with
// zero translation flash before locale negotiation resolves. The full
// ~15.7 KB gz Korean catalog is split into its own async chunk loaded
// via `ensureKoreanBundle()` (same dynamic-import mechanism as the en
// bundle), so a non-ko user no longer carries the full ko catalog on
// the LCP entry chunk — only this boot subset.
import koBoot from "../locales/ko-boot.json";

/**
 * react-i18next primitive bootstrap.
 *
 * Ships the i18n primitives: i18n init, i18next-native plural rules,
 * locale negotiation, the ko + en bundles (both fully populated +
 * key-for-key in sync), runtime switch hook, dynamic `<html lang>`
 * update, backend error wire resolve helper, production hot-reload
 * disable. The ~190 hardcoded Korean strings still in component
 * markup stay as-is until the dedicated wire-in follow-up routes
 * them through `t()` — doing it all in one milestone would smear
 * ADR-009 carry across hundreds of lines and lose the audit grain.
 *
 * **No ICU engine**. The catalogs use single-brace
 * `{var}` interpolation plus exactly three pluralised keys. The
 * `intl-messageformat` / `@formatjs/*` ICU MessageFormat engine
 * (~12 KB gz, parsed at cold-start init on the LCP path) is dropped:
 * the three plural keys are split into i18next-native `_one`/`_other`
 * discrete keys, and the interpolation prefix/suffix below are
 * pinned to single braces so i18next handles `{var}` directly. The
 * two `{{var}}` double-brace keys (`error.internal_with_id` /
 * `error.trace_id_suffix`) stay literal — byte-identical to the
 * prior ICU behavior, which also rendered them literally.
 *
 * **Locale negotiation chain** (highest precedence first):
 *   1. `account.locale` (backend wire — `auth_store.locale`
 *      mirrors the row when login lands)
 *   2. localStorage["arkhe.i18n.locale"] (user's prior runtime
 *      switch — survives reload)
 *   3. `navigator.language` (browser preference — `en*` → `en`)
 *   4. `ko-KR` (cluster fallback)
 *
 * **Production hot-reload disable**: `import.meta.env.PROD`
 * gates debug + missing-key console surface so a production
 * build NEVER ships dev-mode introspection (and the SW cache
 * stays deterministic across reloads).
 */

/** localStorage key that holds the user's last runtime locale
 *  switch. Survives reload + cross-tab via the storage event. */
export const LOCALE_STORAGE_KEY = "arkhe.i18n.locale";

/** Supported locale codes — every bundle ships in `src/locales/
 *  <code>.json`. The current scope covers ko-KR + en; both bundles
 *  are fully populated and key-for-key in sync. */
export const SUPPORTED_LOCALES = ["ko-KR", "en"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Korean is the canonical user-facing locale; the English bundle
 *  is fully populated and key-for-key in sync with it. */
export const FALLBACK_LOCALE: SupportedLocale = "ko-KR";

/**
 * Fixed generic-error LITERAL per locale for the production
 * missing-key fallback. Held as a plain string table — never a
 * bundle key — so the missing-key handler can return user-facing
 * copy WITHOUT re-resolving through `t()`. A `t("error.unknown")`
 * call inside the handler would re-enter the handler if that key
 * were ever absent, recursing until the stack overflows; this
 * table breaks that cycle. The copy mirrors `error.unknown` in
 * each bundle but stays decoupled so it cannot itself go missing.
 */
export const PROD_MISSING_KEY_FALLBACK: Record<SupportedLocale, string> = {
  "ko-KR": "예상치 못한 오류가 발생했어요. 잠시 후 다시 시도해 주세요.",
  en: "Something went wrong. Please try again later.",
};

/**
 * Pick the generic-error literal for the currently-active locale.
 * Reads `i18next.language` (a plain property — no `t()` call, no
 * recursion) and falls back to the cluster default copy when the
 * active language is not one of the bundled locales.
 */
export function prodMissingKeyFallback(): string {
  const active = normaliseLocale(i18next.language);
  return PROD_MISSING_KEY_FALLBACK[active ?? FALLBACK_LOCALE];
}

/**
 * Lazy-load the non-default English bundle + register it on the
 * singleton. The Korean boot subset is statically imported (bundled
 * into the cold-start entry chunk) so the ko-default audience never
 * sees a first-paint translation flash, while the full Korean catalog
 * and the fully-translated English bundle are BOTH split into separate
 * async chunks Vite emits under `assets/` and the `app-locales`
 * CacheFirst PWA rule offline-caches once they land. The dynamic
 * `import("../locales/en.json")` is the code-split point.
 *
 * Idempotent — a second call after the bundle is already registered
 * is a no-op (it does not re-fetch or re-add). Safe to call from both
 * the init path (when the resolved language is `en`) and the runtime
 * switch (`setLocale("en")`).
 */
export async function ensureEnglishBundle(): Promise<void> {
  if (i18next.hasResourceBundle("en", "translation")) return;
  const { default: en } = await import("../locales/en.json");
  // `i18next.addResourceBundle` is a no-op `init` requirement — guard
  // against a racing init that has not finished registering yet by
  // re-checking before the add (addResourceBundle merges either way).
  if (!i18next.hasResourceBundle("en", "translation")) {
    i18next.addResourceBundle("en", "translation", en, true, true);
  }
}

/** Module-level dedup flag for the full Korean catalog. The
 *  `hasResourceBundle("ko-KR", ...)` check `ensureEnglishBundle`
 *  uses cannot serve as the dedup key here — the boot subset is
 *  already registered under `ko-KR` at init, so that check is always
 *  true and would short-circuit before the full catalog ever merges.
 *  This flag mirrors the role en's `hasResourceBundle` guard plays
 *  (en has no eager subset, so its guard doubles as the dedup key). */
let koFullLoaded = false;

/**
 * Lazy-load the FULL Korean catalog + deep-merge it over the eager
 * boot subset on the singleton. The boot subset (`ko-boot.json`)
 * already covers every key the login / app-shell / error-toast render
 * path can surface before locale negotiation resolves, so the ko-
 * default majority paints with no flash; this upgrade brings in the
 * remaining ~12 KB gz of the catalog (table / hand / lobby / settled /
 * etc.) that only renders post-login. The dynamic
 * `import("../locales/ko.json")` is the code-split point — Vite emits
 * it as a separate async chunk that the `app-locales` CacheFirst PWA
 * rule offline-caches once it lands.
 *
 * `addResourceBundle(..., deep=true, overwrite=true)` deep-merges so
 * the full catalog supersedes the boot subset with no key loss.
 * Idempotent via `koFullLoaded` — a second call after the merge is a
 * no-op. Safe to call from both the init path (when the resolved
 * language is `ko-KR`) and the runtime switch (`setLocale("ko-KR")`).
 */
export async function ensureKoreanBundle(): Promise<void> {
  if (koFullLoaded) return;
  const { default: koFull } = await import("../locales/ko.json");
  i18next.addResourceBundle("ko-KR", "translation", koFull, true, true);
  koFullLoaded = true;
}

/**
 * Normalise an arbitrary BCP-47 tag to one of the bundled locales.
 * `ko`, `ko-KR`, `ko-Hang-KR` etc. → `ko-KR`; `en`, `en-US`,
 * `en-GB` etc. → `en`. Returns `null` when no bundled locale
 * matches — the caller falls through to the next negotiation
 * source.
 */
export function normaliseLocale(
  raw: string | null | undefined,
): SupportedLocale | null {
  if (raw === null || raw === undefined) return null;
  const lower = raw.toLowerCase();
  if (lower === "ko" || lower.startsWith("ko-") || lower.startsWith("ko_")) {
    return "ko-KR";
  }
  if (lower === "en" || lower.startsWith("en-") || lower.startsWith("en_")) {
    return "en";
  }
  return null;
}

function readStoredLocale(): SupportedLocale | null {
  if (typeof localStorage === "undefined") return null;
  try {
    return normaliseLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch {
    // Private browsing / quota — fall through to the next source.
    return null;
  }
}

function writeStoredLocale(locale: SupportedLocale): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Non-fatal — the runtime switch still applies for this session.
  }
}

function readNavigatorLocale(): SupportedLocale | null {
  if (typeof navigator === "undefined") return null;
  const languages = (navigator.languages ?? [navigator.language]) as string[];
  for (const candidate of languages) {
    const matched = normaliseLocale(candidate);
    if (matched !== null) return matched;
  }
  return null;
}

/**
 * Apply the active locale to the `<html lang>` attribute so screen
 * readers + browser UI strings pronounce correctly. The OS-level
 * dictation / spell-check tooling also reads this attribute; a
 * stale value here means iOS / Android speak Korean text with
 * English phonemes.
 */
function applyHtmlLang(locale: SupportedLocale): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale;
}

export interface NegotiateLocaleArgs {
  /** Backend-issued account locale (`auth_store.locale` field).
   *  `null` before login / when the row never set a locale
   *  preference. */
  accountLocale?: string | null;
  /** Read from localStorage. Vitest overrides for determinism. */
  storedLocale?: SupportedLocale | null;
  /** Read from navigator.languages. Vitest overrides. */
  navigatorLocale?: SupportedLocale | null;
}

/**
 * Resolve the active locale by walking the negotiation chain in
 * precedence order. Pure helper — vitest passes explicit values
 * for each source so the resolution table is deterministic.
 */
export function negotiateLocale(
  args: NegotiateLocaleArgs = {},
): SupportedLocale {
  const accountMatch = normaliseLocale(args.accountLocale);
  if (accountMatch !== null) return accountMatch;
  const stored =
    args.storedLocale === undefined ? readStoredLocale() : args.storedLocale;
  if (stored !== null) return stored;
  const nav =
    args.navigatorLocale === undefined
      ? readNavigatorLocale()
      : args.navigatorLocale;
  if (nav !== null) return nav;
  return FALLBACK_LOCALE;
}

/**
 * Detect production mode for the hot-reload-disable gate. Falls
 * back to `false` when `import.meta.env` is unavailable (vitest
 * node runtime defaults DEV-ish behaviour for visibility).
 */
function isProductionBuild(): boolean {
  try {
    // `import.meta.env.PROD` is set by Vite at build time.
    // `MODE` is the canonical mode string.
    const env = (import.meta as { env?: { PROD?: boolean; MODE?: string } })
      .env;
    if (env?.PROD === true) return true;
    return env?.MODE === "production";
  } catch {
    return false;
  }
}

/**
 * Initialise the singleton i18next instance. Idempotent — repeat
 * calls re-detect the locale + swap bundles without reloading
 * the React tree (Provider stays mounted; useTranslation hooks
 * receive the language-changed event + re-render).
 */
export function initI18n(args: NegotiateLocaleArgs = {}): I18nType {
  const lng = negotiateLocale(args);
  if (i18next.isInitialized) {
    if (i18next.language !== lng) {
      // Lazy-load the target bundle BEFORE switching so the swap lands
      // on a populated bundle. Both ko (full catalog) and en are code-
      // split now; only the ko boot subset is eager. Fire-and-forget
      // keeps the sync return — the trailing `changeLanguage` emits the
      // bound `languageChanged` event so mounted consumers repaint onto
      // the freshly-merged bundle.
      if (lng === "en") {
        void ensureEnglishBundle().then(() => i18next.changeLanguage(lng));
      } else {
        void ensureKoreanBundle().then(() => i18next.changeLanguage(lng));
      }
      applyHtmlLang(lng);
    }
    return i18next;
  }
  const isProd = isProductionBuild();
  // Init with ONLY the Korean boot subset eagerly bundled (login /
  // app-shell / error-toast keys) so the first paint never awaits +
  // never flashes for the ko-default majority. The full Korean catalog
  // and the non-default English bundle are both lazy (code-split): a
  // cold boot to `ko-KR` fires `ensureKoreanBundle()` below to deep-
  // merge the rest of the catalog in the background, and a cold boot to
  // `en` fires `ensureEnglishBundle()`. Until each lands the fallback
  // chain renders the ko boot subset (no blank UI).
  void i18next.use(initReactI18next).init({
    resources: {
      "ko-KR": { translation: koBoot },
    },
    lng,
    fallbackLng: FALLBACK_LOCALE,
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    // Pin the load strategy to the exact requested language —
    // i18next's default walks the fallback chain on language
    // change (e.g. `ko-KR` → tries `ko` → tries `en`), and the
    // base-code attempt emits `rejecting language code not found
    // in supportedLngs: ko` for every change because our bundle
    // only registers the region-qualified `ko-KR`. `currentOnly`
    // suppresses that walk; the explicit `fallbackLng` above
    // still handles the eventual "no exact match" branch.
    load: "currentOnly",
    // Production hot-reload disable. The PROD gate keeps debug
    // logs + saveMissing POSTs out of shipped builds; dev keeps
    // them on so a missing key surfaces immediately in the
    // console.
    debug: !isProd,
    saveMissing: false,
    // Missing-key fallback. Dev surfaces the literal key with
    // the `[[…]]` brackets so the gap is obvious; production
    // returns a fixed generic literal so user-facing copy never
    // goes blank. The handler MUST NOT re-resolve a key (no
    // `t(...)` call) — a self-referential lookup against a key
    // that is itself missing would re-enter this handler and
    // recurse until the stack overflows.
    parseMissingKeyHandler: (key) =>
      isProd ? prodMissingKeyFallback() : `[[${key}]]`,
    interpolation: {
      // React already escapes by default — i18next doesn't need
      // to double-encode.
      escapeValue: false,
      // Single-brace `{var}` interpolation (the catalog
      // convention, ~210 tokens) is handled natively by i18next via
      // these prefix/suffix overrides, so the ICU MessageFormat
      // engine is no longer needed on the cold-start path. i18next's
      // default is `{{var}}`; pinning to single braces keeps every
      // existing `{var}` token resolving exactly as the prior ICU
      // engine did, while `{{var}}` double-brace keys pass through
      // literally (the prior ICU engine rendered them literally too).
      prefix: "{",
      suffix: "}",
    },
    // `react-i18next` Suspense off — the Korean boot subset is
    // sync-bundled so the first paint never blocks. The full ko
    // catalog and the en bundle load async, but with Suspense off the
    // tree renders the ko boot subset immediately + re-renders on the
    // `languageChanged` event when the deferred chunk lands (no
    // spinner, no blank gate).
    react: { useSuspense: false },
  });
  applyHtmlLang(lng);
  // Negotiated to English on a cold boot — pull the lazy en chunk +
  // switch once it resolves. Fire-and-forget so the sync return holds;
  // until it lands the fallback chain shows the ko boot subset (no
  // flash for the ko-default majority, who never enter this branch).
  if (lng === "en") {
    void ensureEnglishBundle().then(() => i18next.changeLanguage("en"));
  } else if (lng === "ko-KR") {
    // Negotiated to Korean — the boot subset already painted
    // synchronously above (zero flash). Pull the full catalog in the
    // background and deep-merge the remaining keys. The trailing
    // `changeLanguage("ko-KR")` is load-bearing: react-i18next 17's
    // `bindI18nStore` defaults to `''`, so the bare `addResourceBundle`
    // merge inside `ensureKoreanBundle` emits the i18next store `added`
    // event but NO event bound by mounted `useTranslation` consumers —
    // a first-paint component that resolved a non-boot key would keep
    // showing the missing-key fallback until an unrelated re-render.
    // `changeLanguage("ko-KR")` is a no-op for the active language but
    // emits the `languageChanged` event (bound by default), forcing the
    // bound repaint that flips every consumer onto the full catalog.
    void ensureKoreanBundle().then(() => i18next.changeLanguage("ko-KR"));
  }
  return i18next;
}

/**
 * Apply a user-initiated locale switch (HUD menu item). Persists
 * the choice to localStorage so the next session boots straight
 * into the chosen language. Idempotent — a re-apply with the same
 * code is a no-op. Side effect: updates `<html lang>` so screen
 * readers re-cue with the new pronunciation tag.
 */
export async function setLocale(locale: SupportedLocale): Promise<void> {
  if (!SUPPORTED_LOCALES.includes(locale)) return;
  writeStoredLocale(locale);
  // Switching INTO either locale pulls its lazy bundle first so
  // `changeLanguage` lands on a populated resource set (the await is
  // user-gesture-bound, never on the first paint). Switching to ko
  // deep-merges the full catalog over the eager boot subset (no-op
  // once `koFullLoaded`); switching to en pulls the en async chunk.
  if (locale === "en") {
    await ensureEnglishBundle();
  } else {
    await ensureKoreanBundle();
  }
  if (!i18next.isInitialized) {
    initI18n({ storedLocale: locale });
    return;
  }
  if (i18next.language !== locale) {
    await i18next.changeLanguage(locale);
  }
  applyHtmlLang(locale);
}

/** Singleton i18next instance — re-exported so direct callers
 *  (test setup, error helpers) can read `i18next.t(...)` without
 *  going through the React hook. */
export { i18next };
